import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AuthserviceService } from './authservice.service';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { CompoApp } from '../../../../services/CompoApp';
import ErrorCode from '../../../../enums/ErrorCode';
import { filter, takeUntil } from 'rxjs/operators';
import { UnsubscribeBaseModel } from '../../../../base/model/UnsubscribeBase.model';
import {
  GlobalMessageClientService
} from '../../../../services/client/global-message-client-service/global-message-client.service';
import { SseSingletonService } from '../../../../services/sse/sse-singleton.service';
import { PropagateOpts } from '../../../../models/form/base-cp-form.model';
import Constants from '../../../../Constants';
import { environment } from '../../../../../environments/environment';
import { AuthPageCommonComponent } from '../auth-page-common.component';
import { FormInputComponent } from '../../../components/input/form-input/form-input.component';
import { InlineButtonComponent } from '../../../globals/button/inline-button/inline-button.component';
import { ButtonComponent } from '../../../globals/button/button/button.component';
import {
  MessageDesignBSmallComponent
} from '../../../components/message-design-b/message-design-b-small/message-design-b-small.component';
import {
  MessageDesignBGlobalComponent
} from '../../../components/message-design-b/message-design-b-global/message-design-b-global.component';
import {
  MessageDesignBRelativeContentComponent
} from '../../../components/message-design-b/message-design-b-relativ-content/message-design-b-relative-content.component';
import { TranslateModule } from '@ngx-translate/core';
import { USER_ID } from '../../../../shared.app.rights.context';

@Component({
  selector: 'cp-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss', '../auth-page-common.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [AuthPageCommonComponent, FormInputComponent, InlineButtonComponent, ButtonComponent, MessageDesignBSmallComponent, MessageDesignBGlobalComponent, MessageDesignBRelativeContentComponent, TranslateModule]
})
export class LoginPageComponent extends UnsubscribeBaseModel implements OnInit {

  //region INJECTIONS
  protected messageService = inject(GlobalMessageClientService);
  private sseSingletonService = inject(SseSingletonService);
  private authService: AuthserviceService = inject(AuthserviceService);
  private router: Router = inject(Router);
  private app: CompoApp = inject(CompoApp);
  private titleService: Title = inject(Title);
  private route: ActivatedRoute = inject(ActivatedRoute);
  //endregion

  //region FORM
  formGroup: UntypedFormGroup = new UntypedFormGroup({});
  public readonly LOGIN: string = 'login';
  public readonly PASSWORD: string = 'password';
  public readonly STAY_LOGGED_IN: string = 'stay logged in';
  //endregion

  //region FIELDS
  isMessageVisible: boolean = false;
  loginFailed: boolean = false;
  loginFailedReason: any = null;
  showPlaceHolder: boolean;
  inProgress: boolean = false;
  redirectUrl: string;
  //endregion

  //region GETTERS
  get loginFailedText(): string {
    if (!this.loginFailedReason) {
      return null;
    }

    switch (this.loginFailedReason) {
      case ErrorCode.USER_INACTIVE_OR_NO_ACTIVE_MAPPINGS: {
        return 'MESSAGE_TEXTS.LOGIN.USER_INACTIVE_OR_NO_ACTIVE_MAPPINGS';
      }
      case ErrorCode.WRONG_USERNAME_OR_PASSWORD:
      default: {
        return 'MESSAGE_TEXTS.LOGIN.WRONG_USER_NAME_OR_PASSWORD';
      }
    }
  }

  get isValid(): boolean {
    return this.formGroup?.valid;
  }

  //endregion

  //region LIFECYCLE
  ngOnInit() {
    this.titleService.setTitle('Objack');
    this.createFormGroup();
    this.redirectUrl = this.getRedirectUrl();
    if (this.authService.isAuthenticated() && this.redirectUrl) {
      window.location.href = environment.serverUrlBase + this.redirectUrl + Constants.URLS.query.parameter.accessToken + this.app.data.getToken();
    }
    this.formGroup.valueChanges
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe(value => {
        if (
          (value[this.LOGIN] != null || value[this.PASSWORD] != null) &&
          (!this.formGroup.get(this.LOGIN).hasValidator(Validators.required) || !this.formGroup.get(this.PASSWORD).hasValidator(Validators.required))
        ) {
          this.setLoginOrPasswordRequired();
        }
      });

    this.app.on.serverUnreachable$
      .pipe(takeUntil(this.unSubscribe$), filter(v => v != false))
      .subscribe((v) => {
        this.isMessageVisible = true;
      });
  }

  //endregion

  //region METHODS
  onResetUserPassword() {
    this.router.navigate(['/forgotPassword']);
  }

  onLoginFormSubmit() {
    if (!this.isValid || this.inProgress) {
      return;
    }

    const login = this.formGroup.get(this.LOGIN).value;
    const password = this.formGroup.get(this.PASSWORD).value;

    if (!login || !password) {
      this.setLoginOrPasswordRequired();
      return;
    }

    this.formGroup.disable(PropagateOpts.EMIT_EVENT_FALSE);
    this.inProgress = true;

    this.authService.login(login, password)
      .subscribe({
        next: result => {
          if (!result?.token) {
            return;
          }
          if (this.redirectUrl) {
            window.location.href = environment.serverUrlBase + this.redirectUrl + Constants.URLS.query.parameter.accessToken + result.token;
            return;
          }
          this.app.on.serverUnreachable$.next(false);
          this.authService.initAccessModeFromRoleId(result.selectedUserRoleId);
          let pendingUrl: string;
          if (result.roleCount <= 1) {
            this.getUserRightInfosOfUser();
            pendingUrl = this.app.data.getPendingUrl();
            this.app.data.setPendingUrl(null);
          }

          this.sseSingletonService.disconnect();
          this.sseSingletonService.connect();
          this.titleService.setTitle('Objack');

          if (this.app.data.getUser().isResetPasswordOnLogin) {
            this.router.navigate(['/resetPassword/' + this.app.data.getUser().id]);
          } else if (result.roleCount > 1) {
            this.router.navigate(['/selectRole'], { queryParams: { roleCount: result.roleCount } });
          } else {
            this.app.on.loginTrigger.next(this.app.data.getUser());
            this.router.navigate([pendingUrl || '/home/dashboard']);
          }
        },
        error: error => {
          if (error.status === 401) {
            if (error.error && error.error.code && error.error.code === ErrorCode.WRONG_USERNAME_OR_PASSWORD) {
              this.loginFailed = true;
              this.loginFailedReason = ErrorCode.WRONG_USERNAME_OR_PASSWORD;
            }
            if (error.error && error.error.code && error.error.code === ErrorCode.USER_INACTIVE_OR_NO_ACTIVE_MAPPINGS) {
              this.loginFailed = true;
              this.loginFailedReason = ErrorCode.USER_INACTIVE_OR_NO_ACTIVE_MAPPINGS;
            }
            this.formGroup.reset();
            this.sseSingletonService.disconnect();
          }
          this.formGroup.enable(PropagateOpts.EMIT_EVENT_FALSE);
          this.inProgress = false;
        }
      });
  }

  private setLoginOrPasswordRequired(): void {
    this.formGroup.get(this.LOGIN).addValidators(Validators.required);
    this.formGroup.get(this.PASSWORD).addValidators(Validators.required);
    this.formGroup.get(this.LOGIN).updateValueAndValidity();
    this.formGroup.get(this.PASSWORD).updateValueAndValidity();
    this.showPlaceHolder = true;
  }

  private getUserRightInfosOfUser() {
    USER_ID.next(this.app.data.getUser()?.id);
  }

  private createFormGroup() {
    this.formGroup.addControl(this.LOGIN, new UntypedFormControl(null));
    this.formGroup.addControl(this.PASSWORD, new UntypedFormControl(null));
    this.formGroup.addControl(this.STAY_LOGGED_IN, new UntypedFormControl(null));
  }

  //endregion
  private getRedirectUrl() {
    const redirectPath = this.route.snapshot.queryParamMap.get('redirect');
    const targetId = this.route.snapshot.queryParamMap.get('targetId');
    switch (redirectPath) {
      case 'document':
        return '/rest/document-data/' + targetId;
      default:
        return null;
    }
  }
}
